import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-at";
const RELOAD_WINDOW_MS = 15000;

/**
 * Lazy import that survives stale hashed chunks after a new deploy:
 * 1. retry the import once with a cache-busting query
 * 2. if that still fails, force one full page reload (rate-limited)
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      try {
        // Bust the module cache and try once more.
        const url = new URL(
          (error as { url?: string })?.url ?? "",
          window.location.href
        );
        if (url.pathname && url.pathname !== "/") {
          url.searchParams.set("v", Date.now().toString());
          return (await import(/* @vite-ignore */ url.toString())) as {
            default: T;
          };
        }
      } catch {
        // fall through to reload
      }

      const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
      if (Date.now() - last > RELOAD_WINDOW_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
      throw error;
    }
  });
}
